import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useRef } from "react";
import * as styles from "./email-message.module.css";

export type MessageContent =
  | { type: "messageText"; messageText: string }
  | { type: "messageHtml"; messageHtml: string };

export interface SimpleEmailMessageProps {
  messageContent: MessageContent;
  className?: string;
}

function convertPlainTextToHtml(plainText: string) {
  return plainText.replace(/\n/g, "<br>");
}

export const SimpleEmailMessage = memo(function SimpleEmailMessage({
  messageContent,
  className,
}: SimpleEmailMessageProps) {
  const emailIframeRef = useRef<HTMLIFrameElement>(null);

  const messageHTML =
    messageContent.type === "messageHtml"
      ? messageContent.messageHtml
      : convertPlainTextToHtml(messageContent.messageText);

  useEffect(() => {
    if (emailIframeRef.current) {
      // Process message for safe display
      let processedMessage = messageHTML;

      // Convert image links to actual images
      processedMessage = processedMessage.replace(
        /(https?:\/\/\S*\.(?:png|jpg|gif|jpeg|webp))(?![^<]*>)/g,
        (match) => `<br /><img src="${match}" />`,
      );

      // Remove &nbsp; characters (quill bug workaround)
      processedMessage = processedMessage.replace(/&nbsp;/g, " ");

      const iframeContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body {
              margin: 0;
              padding: 16px;
              font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              color: var(--redo-colors-text-text-primary-900, #000);
              background: transparent;
            }
            p {
              margin-block-start: 0;
              margin-block-end: 0;
            }
            img {
              max-height: 800px;
              max-width: 100%;
            }
            pre {
              white-space: pre-wrap;
            }
            a {
              color: var(--redo-colors-semantic-link-500, #0066cc);
              text-decoration: underline;
            }
            a:hover {
              text-decoration: none;
            }
          </style>
        </head>
        <body>
          ${processedMessage}
        </body>
        </html>
      `;

      const iframeDoc =
        emailIframeRef.current.contentDocument ||
        emailIframeRef.current.contentWindow?.document;

      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(iframeContent);
        iframeDoc.close();

        // Make links open in parent window
        const links = iframeDoc.querySelectorAll("a[href]");
        for (const link of links) {
          link.setAttribute("target", "_parent");
          link.setAttribute("rel", "noopener noreferrer");
        }

        // Function to update iframe height
        const updateIframeHeight = () => {
          if (emailIframeRef.current && iframeDoc.body) {
            const contentHeight = iframeDoc.body.scrollHeight;
            emailIframeRef.current.style.height = `${contentHeight}px`;
          }
        };

        const iframeWindow = emailIframeRef.current.contentWindow;
        if (iframeWindow) {
          //need to wait for iframe to fully load and then update the height
          iframeWindow.addEventListener("load", updateIframeHeight);
        }
      }
    }
  }, [messageHTML]);

  return (
    <Flex className={className} w="full">
      <iframe
        className={styles.emailIframe}
        ref={emailIframeRef}
        sandbox="allow-top-navigation allow-downloads allow-forms allow-same-origin"
        style={{
          width: "100%",
          border: "none",
          background: "transparent",
          height: "auto",
          overflow: "hidden",
        }}
        title="Email content"
      />
    </Flex>
  );
});
